import { useEffect, useState } from "react";
import { X, Link2, Copy, Mail, Send } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function BookingLinkModal({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/booking/config").then((r) => {
      setCfg(r.data);
      setEmailSubject(r.data.booking_email_subject);
      setEmailMsg(r.data.booking_email_message);
    }).catch(() => toast.error("Impossible de charger le lien de RDV"));
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(cfg.link);
      toast.success("Lien copié dans le presse-papiers");
    } catch { toast.error("Copie impossible — sélectionnez le lien manuellement"); }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/booking/send-link", {
        channel: "email", to: emailTo.trim(), subject: emailSubject, message: emailMsg,
      });
      if (data.ok) {
        toast.success("Email envoyé");
        onClose();
      } else {
        toast.error("Échec de l'envoi — vérifiez le destinataire");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const inputCls = "w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-400 transition-colors";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="booking-link-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 px-6 py-4 border-b border-neutral-100">
          <div className="w-8 h-8 rounded-lg bg-neutral-900 flex items-center justify-center">
            <Link2 className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading font-bold">Lien de prise de RDV</h2>
            <p className="text-xs text-neutral-400">Vos clients réservent directement dans votre agenda.</p>
          </div>
          <button data-testid="booking-modal-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!cfg ? (
          <div className="py-16 flex justify-center"><div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2">
              <p className="flex-1 text-xs text-neutral-600 truncate font-mono" data-testid="booking-link-url">{cfg.link}</p>
              <button data-testid="booking-copy-link" onClick={copyLink}
                className="flex items-center gap-1.5 text-xs font-medium bg-neutral-900 text-white rounded-md px-2.5 py-1.5 hover:bg-neutral-800 transition-colors">
                <Copy className="w-3 h-3" /> Copier
              </button>
            </div>

            <div className="space-y-3" data-testid="booking-email-form">
              <p className="text-xs font-semibold text-neutral-700 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-blue-600" /> Envoyer le lien par email
              </p>
              <div>
                <label className="text-xs font-medium text-neutral-600">Adresse email du destinataire</label>
                <input data-testid="booking-email-to" type="email" className={`${inputCls} mt-1`} placeholder="client@exemple.com"
                  value={emailTo} onChange={(e) => setEmailTo(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600">Objet</label>
                <input data-testid="booking-email-subject" className={`${inputCls} mt-1`}
                  value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600">Message (le lien remplace {"{lien}"})</label>
                <textarea data-testid="booking-email-message" rows={5} className={`${inputCls} mt-1 resize-none`}
                  value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} />
              </div>
            </div>

            <button data-testid="booking-send-btn" onClick={send} disabled={sending || !emailTo.trim()}
              className="w-full flex items-center justify-center gap-2 bg-neutral-900 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-neutral-800 transition-colors disabled:opacity-50">
              <Send className="w-4 h-4" /> {sending ? "Envoi…" : "Envoyer par email"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
